import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';

// eslint-disable-next-line import/no-cycle
import { API } from 'app/shared/constants/app-service-constants';
import { PlatformService } from './platform.service';
import * as fromRoot from '../store';
import { refresh } from '../../inbox/inbox-deliveries/store/inbox.actions';

@Injectable({
  providedIn: 'root',
})
export class PushService {
  isModalClosed: Subject<void> = new Subject<void>();

  private defaultNotification = {
    body: 'New push notification received',
    deliveryUrl: '',
    title: 'New notification',
  };

  constructor(
    private router: Router,
    private alertCtrl: AlertController,
    private platformService: PlatformService,
    private platform: Platform,
    private store: Store<fromRoot.State>,
    private ngZone: NgZone,
    private http: HttpClient,
  ) {}

  redirect(deeplink, url = ''): void {
    this.isModalClosed.next();
    this.store.dispatch(refresh());

    if (deeplink) {
      const path = deeplink?.includes('delivery')
        ? deeplink.slice(deeplink.indexOf('/delivery'))
        : '';
      this.router.navigateByUrl(path);
      return;
    }

    if (url) {
      window.open(url, '_system');
    }
  }

  firebaseInit() {
    if (!this.platformService.isCordova()) {
      return;
    }
    this.platform.ready().then(async () => {
      PushNotifications.requestPermissions()
        .then((result) => {
          if (result.receive === 'granted') {
            // Register with Apple / Google to receive push via APNS/FCM
            PushNotifications.register();
          } else {
            console.log('push notification failed');
          }
        })
        .catch((error: any) => console.error('hasPermission Error getting token', error));

      this.listenOnToken();
      this.listenOnError();

      // // Show us the notification payload if the app is open on our device
      PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          this.ngZone.run(() => {
            setTimeout(() => {
              const { data } = notification;

              const params = {
                deliveryUrl: data?.deliveryUrl,
                title: data?.title || data?.aps?.alert?.title || notification?.title,
                body: data?.body || data?.aps?.alert?.body || notification?.body,
              };
              console.log(params.body, params.title, params.deliveryUrl);
              console.log({ notification });

              this.showNotification(params?.body ? params : this.defaultNotification);
            }, 1000);
            return;
          });
        },
      );

      // // Method called when tapping on a notification
      PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action: ActionPerformed) => {
          this.ngZone.run(() => {
            if (action?.notification?.data) {
              this.redirect(action?.notification?.data?.deliveryUrl);
            }
          });
        },
      );
    });
  }

  async unregister() {
    if (this.platformService.isCordova()) {
      PushNotifications.removeAllListeners();
    }
  }

  async showNotification(data: { title: string; body: string; deliveryUrl: string }) {
    const alert = await this.alertCtrl.create({
      header: `${data.title}`,
      message: data.body,
      buttons: ['OK'],
    });
    alert.present();

    await alert.onDidDismiss();
    this.ngZone.run(() => {
      this.redirect(data?.deliveryUrl);
    });
  }

  private listenOnToken(): void {
    PushNotifications.addListener('registration', (token: Token) => {
      if (token) {
        this.updateFirebaseToken(token.value);
      }
    });
  }

  private listenOnError(): void {
    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Error on registration: ' + JSON.stringify(error));
    });
  }

  private updateFirebaseToken(firebaseToken) {
    const deviceId = this.platformService.getDeviceId();

    this.http
      .put(API.UPDATE_FIREBASE_TOKEN, {
        deviceId,
        firebaseToken,
      })
      .subscribe();
  }
}
